// Notificaciones en (casi) tiempo real vía polling: cada POLL_MS se
// consulta el backend y, si aparece una notificación nueva no vista,
// se expone en `justArrived` para mostrar un toast/alerta puntual.
import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../api";
import { useAuth } from "../context/AuthContext";

const POLL_MS = 15000;

export function useNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [justArrived, setJustArrived]     = useState(null); // última notificación nueva no vista
  const seenIds = useRef(new Set());
  const firstLoad = useRef(true);

  // Solo auxiliares y usuarios de empresa reciben notificaciones (admin no las usa).
  const canReceive = user?.role === "auxiliar" || user?.role === "empresa";

  const load = useCallback(async () => {
    if (!canReceive) return;
    try {
      const data = await api.getNotifications();
      if (!Array.isArray(data)) return;
      setNotifications(data);

      // En la primera carga no se dispara el toast de "nueva notificación"
      // (todo lo que ya existía se trata como visto, no como recién llegado).
      if (!firstLoad.current) {
        const fresh = data.find(n => !n.read && !seenIds.current.has(n.id));
        if (fresh) setJustArrived(fresh);
      }
      firstLoad.current = false;
      data.forEach(n => seenIds.current.add(n.id));
    } catch {
      // silencioso: si el backend no responde, no interrumpir la UI
    }
  }, [canReceive]);

  useEffect(() => {
    if (!canReceive) return;
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [canReceive, load]);

  const markRead = async (id) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    await api.markNotificationRead(id);
  };

  const markAllRead = async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    await api.markAllNotificationsRead();
  };

  const dismissJustArrived = () => setJustArrived(null);

  const deleteNotification = async (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
    await api.deleteNotification(id);
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return { notifications, unreadCount, markRead, markAllRead, justArrived, dismissJustArrived, deleteNotification };
}
